import React, { useEffect, useState, useCallback } from 'react';
import { LogOut, Clock, AlertTriangle } from 'lucide-react';
import { SimulatorModal } from './simulator/SimulatorModal';
import { supabase } from '../lib/supabase';

interface GuestSimulatorDashboardProps {
  user: any;
  invitation: any;
  onLogout: () => void;
  availableServices?: string[];
  onSwitchService?: () => void;
}

export function GuestSimulatorDashboard({ user, invitation: initialInvitation, onLogout, availableServices = [], onSwitchService }: GuestSimulatorDashboardProps) {
  const [daysLeft, setDaysLeft] = useState<number>(0);
  const [hoursLeft, setHoursLeft] = useState<number>(0);
  const [isExpired, setIsExpired] = useState(false);
  const [expiresAt, setExpiresAt] = useState<string | null>(initialInvitation?.expires_at ?? null);

  const computeTimeLeft = useCallback((expiryDate: string | null) => {
    if (!expiryDate) return;
    const now = new Date();
    const expires = new Date(expiryDate);
    const diff = expires.getTime() - now.getTime();
    if (diff <= 0) {
      setIsExpired(true);
      setDaysLeft(0);
      setHoursLeft(0);
    } else {
      const totalHours = diff / (1000 * 60 * 60);
      const days = Math.floor(totalHours / 24);
      const hours = Math.floor(totalHours % 24);
      setDaysLeft(days);
      setHoursLeft(hours);
    }
  }, []);

  const refreshInvitation = useCallback(async () => {
    if (!user?.email) return;
    const { data } = await supabase
      .from('simulator_invitations')
      .select('expires_at, validated_at')
      .eq('email', user.email)
      .maybeSingle();
    if (data) {
      setExpiresAt(data.expires_at);
      computeTimeLeft(data.expires_at);
    }
  }, [user?.email, computeTimeLeft]);

  useEffect(() => {
    computeTimeLeft(expiresAt);
  }, [expiresAt, computeTimeLeft]);

  useEffect(() => {
    const interval = setInterval(() => {
      refreshInvitation();
    }, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [refreshInvitation]);

  if (isExpired) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-7 h-7 text-red-500" />
          </div>
          <h2 className="text-lg font-semibold text-neutral-900 mb-2">Accès expiré</h2>
          <p className="text-sm text-neutral-500 mb-6">Votre période d'accès au simulateur est terminée. Contactez l'administrateur pour renouveler votre accès.</p>
          <button
            onClick={onLogout}
            className="w-full py-2.5 rounded-xl border border-neutral-200 text-sm font-medium text-neutral-600 hover:bg-neutral-50 transition-colors"
          >
            Retour au site
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-neutral-50">
      <div className="flex items-center justify-between px-6 py-3 bg-white border-b border-neutral-100 shrink-0">
        <div className="flex items-center gap-3">
          <img src="/images/logo-v2.png" alt="Sun Is Up" className="h-8 object-contain" />
          <span className="text-sm font-medium text-neutral-500">Accès simulateur</span>
        </div>
        <div className="flex items-center gap-4">
          {(daysLeft > 0 || hoursLeft > 0) && (
            <div className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full ${daysLeft <= 3 ? 'bg-amber-100 text-amber-700' : 'bg-neutral-100 text-neutral-500'}`}>
              <Clock className="w-3.5 h-3.5" />
              {daysLeft > 0
                ? <span>{daysLeft} jour{daysLeft > 1 ? 's' : ''} restant{daysLeft > 1 ? 's' : ''}</span>
                : <span>{hoursLeft}h restante{hoursLeft > 1 ? 's' : ''}</span>
              }
            </div>
          )}
          <span className="text-xs text-neutral-400 hidden sm:block">{user.email}</span>
          {onSwitchService && availableServices.length > 1 && (
            <button
              onClick={onSwitchService}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-neutral-800 bg-[#FFF9EC] border border-[#F8BD3D] hover:bg-[#FFF3D0] rounded-lg transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
              Changer de service
            </button>
          )}
          <button
            onClick={onLogout}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 rounded-lg transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            Quitter
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-hidden">
        <SimulatorModal
          isOpen={true}
          onClose={onLogout}
          guestMode={true}
          guestCountdown={{ daysLeft, hoursLeft }}
        />
      </div>
    </div>
  );
}
